/** The backend only sends `pathwayUpgrades` when some level has options. */
export const isPathwayEvolution = (evolution) => (evolution.pathwayUpgrades?.length ?? 0) > 0;
/** A level with a single option is fixed; only multi-option levels are a choice. */
const pathwayLevelHasChoice = (options) => options.length > 1;
/**
 * Option 0 at every level — the choice a step is born with. A path is always
 * selected; there is no "none picked" state. Linear evolutions get `[]`.
 */
export const defaultPathwayPath = (evolution) => isPathwayEvolution(evolution) ? evolution.pathwayUpgrades.map(() => 0) : [];
/**
 * The option index chosen at `level`, clamped to what exists so a stale or
 * malformed URL never indexes past the options.
 */
const selectedPathwayOption = (options, pathwayPath, level) => {
    const chosen = pathwayPath?.[level] ?? 0;
    return chosen >= 0 && chosen < options.length ? chosen : 0;
};
/**
 * What one level grants under a given choice: the level's fixed upgrades,
 * then the chosen option's. Returns `upgrades[level]` untouched on a linear
 * evolution.
 */
const upgradesForLevel = (evolution, pathwayPath, level) => {
    const fixed = evolution.upgrades[level] ?? [];
    const options = evolution.pathwayUpgrades?.[level];
    if (!options?.length)
        return fixed;
    const option = options[selectedPathwayOption(options, pathwayPath, level)];
    return [...fixed, ...asUpgrades(option)];
};
// A fixture has sent a flat object where an option list belongs; better an
// empty option than a crashed page.
const asUpgrades = (option) => Array.isArray(option) ? option : [];
/**
 * Every level resolved under a choice, in application order. This is the
 * shape `Evolution.upgrades` already has for a linear evolution, so anything
 * summing or listing upgrades can take this instead and stay pathway-blind.
 */
export const upgradesForPathway = (evolution, pathwayPath) => {
    const levelCount = Math.max(evolution.upgrades.length, evolution.pathwayUpgrades?.length ?? 0);
    return Array.from({ length: levelCount }, (_, level) => upgradesForLevel(evolution, pathwayPath, level));
};
/**
 * Every level of an evolution under a choice, linear or pathway. A linear
 * level is a single option nobody can change, so one renderer draws both.
 */
export const evolutionLevels = (evolution, pathwayPath) => {
    const levelCount = Math.max(evolution.upgrades.length, evolution.pathwayUpgrades?.length ?? 0);
    return Array.from({ length: levelCount }, (_, level) => {
        const fixed = evolution.upgrades[level] ?? [];
        const options = evolution.pathwayUpgrades?.[level] ?? [];
        if (options.length === 0) {
            return { level, options: [fixed], selectedOption: 0, hasChoice: false };
        }
        return {
            level,
            options: options.map((option) => [...fixed, ...asUpgrades(option)]),
            selectedOption: selectedPathwayOption(options, pathwayPath, level),
            hasChoice: pathwayLevelHasChoice(options),
        };
    });
};
/** "A", "B", ... — how a level's options are named to the user. */
export const pathwayOptionLetter = (option) => String.fromCharCode(65 + option);
/**
 * The chosen path as the user reads it: one letter per level that offers a
 * choice, in level order — "B", or "A, B" across two choices. A level with a
 * single option says nothing, so the label only ever names real decisions.
 * Undefined when there is nothing to choose.
 */
export const pathwayPathLabel = (evolution, pathwayPath) => {
    const letters = (evolution.pathwayUpgrades ?? []).flatMap((options, level) => pathwayLevelHasChoice(options)
        ? [
            pathwayOptionLetter(selectedPathwayOption(options, pathwayPath, level)),
        ]
        : []);
    return letters.length > 0 ? letters.join(", ") : undefined;
};
/** `pathwayPath` with one level's choice replaced; other levels untouched. */
export const withPathwayOption = (pathwayPath, level, option) => {
    const next = [...pathwayPath];
    while (next.length <= level)
        next.push(0);
    next[level] = option;
    return next;
};
// ---------------------------------------------------------------------------
// How a level card draws each upgrade.
// ---------------------------------------------------------------------------
/** The upgrade keys the backend uses for a granted role. */
export const ROLE_PLUS_KEYS = ["rolePlus", "rolePlusPlus"];
export const isRolePlusUpgrade = (upgrade) => ROLE_PLUS_KEYS.includes(upgrade.key);
/**
 * A render key per upgrade, stable across a path switch: keyed by what it
 * grants and how often that has appeared, not by position, so a stat both
 * paths grant keeps its identity while a different PlayStyle or role does not.
 */
export const levelUpgradeKeys = (upgrades) => {
    const seen = new Map();
    return upgrades.map((upgrade) => {
        const identity = upgrade.type === "playstyle"
            ? `${upgrade.key}:${upgrade.id}:${upgrade.isPlus}`
            : isRolePlusUpgrade(upgrade)
                ? `${upgrade.key}:${upgrade.value}`
                : upgrade.key;
        const nth = seen.get(identity) ?? 0;
        seen.set(identity, nth + 1);
        return `${identity}:${nth}`;
    });
};
//# sourceMappingURL=pathway.js.map